'use strict';

/* eslint-disable @typescript-eslint/no-var-requires */

// Amazon S3 browser-upload protocols. Single-part uploads use a create-only
// precondition and an ownership token. Multipart uploads persist the opaque
// provider session in the backend; authorization re-lists S3's uploaded parts
// so a new browser can resume without trusting local bookkeeping.

const {
  UploadHttpError,
  UploadSessionRestartError,
  applyUploadAuthorization,
  fetchWithRetry,
  reportProgress,
  throwIfAborted,
} = require('./core');

async function runS3SinglePart(upload, options) {
  let signedUrl = upload.record.signedUrl;
  let ownershipToken = upload.record.sessionUri;
  const refreshAuthority = async () => {
    const authorization = await upload.api.authorize(upload.record.id, []);
    signedUrl = authorization.signedUrl;
    ownershipToken = authorization.sessionUri;
    upload.record.signedUrl = signedUrl;
    upload.record.sessionUri = ownershipToken;
    if (!signedUrl || !ownershipToken) {
      throw new Error('S3 did not return single-part upload authority.');
    }
  };
  if (!signedUrl || !ownershipToken) await refreshAuthority();

  await reportProgress(
    upload,
    upload.api,
    upload.onProgress,
    0,
    false,
    options.signal
  );

  try {
    await fetchWithRetry(
      () =>
        options.fetchImpl(signedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
            // Never overwrite a key that appeared after upload creation.
            'If-None-Match': '*',
            // The backend verifies this signed metadata before accepting a
            // pre-existing object as the result of this upload.
            'X-Amz-Meta-Cloud-Analytics-Upload-Token': ownershipToken,
          },
          body: upload.file,
          mode: 'cors',
          signal: options.signal,
        }),
      {
        ...options,
        operation: 'Uploading the S3 object',
        accepted: (candidate) => candidate.ok,
        refreshAuthority,
      }
    );
  } catch (error) {
    if (error instanceof UploadHttpError && [409, 412].includes(error.status)) {
      // A prior request may have stored the object but lost its response. The
      // backend verifies token and size; it never trusts the status alone.
      throwIfAborted(options.signal);
      const completed = await upload.api.complete(upload.record.id);
      throwIfAborted(options.signal);
      return completed;
    }
    throw error;
  }

  await reportProgress(
    upload,
    upload.api,
    upload.onProgress,
    upload.file.size,
    false,
    options.signal
  );
  return null;
}

function uploadedPartMap(parts) {
  return new Map(
    (parts || [])
      .filter((part) => part && part.etag && part.size != null)
      .map((part) => [part.partNumber, part])
  );
}

async function authorizeS3Part(upload, partNumber) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const authorization = await upload.api.authorize(upload.record.id, [
      partNumber,
    ]);
    if (applyUploadAuthorization(upload, authorization)) {
      // The old ETag list belongs to a different provider upload ID.
      throw new UploadSessionRestartError();
    }

    const uploaded = uploadedPartMap(authorization.parts);
    if (uploaded.has(partNumber)) return { uploaded: uploaded.get(partNumber) };
    const part = (authorization.parts || []).find(
      (candidate) =>
        candidate?.partNumber === partNumber && candidate?.signedUrl
    );
    if (part) return { signedUrl: part.signedUrl };
  }
  throw new Error(`S3 did not authorize upload part ${partNumber}.`);
}

async function runS3MultipartSession(upload, options) {
  const initial = await upload.api.authorize(upload.record.id, []);
  applyUploadAuthorization(upload, initial);
  const partSize = Number(upload.record.partSize);
  if (!Number.isInteger(partSize) || partSize <= 0) {
    throw new Error('S3 multipart upload is missing a valid part size.');
  }

  // The backend is authoritative for uploaded parts. This lets a new browser
  // session skip bytes already accepted by S3.
  const existing = uploadedPartMap(initial.parts);
  let uploadedBytes = [...existing.values()].reduce(
    (total, part) => total + Number(part.size || 0),
    0
  );
  await reportProgress(
    upload,
    upload.api,
    upload.onProgress,
    uploadedBytes,
    false,
    options.signal
  );

  // Parts are intentionally sequential: progress persistence and provider
  // session replacement remain deterministic and resumable.
  const partCount = Math.ceil(upload.file.size / partSize);
  for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
    if (existing.has(partNumber)) continue;
    let authority = await authorizeS3Part(upload, partNumber);
    if (authority.uploaded) {
      existing.set(partNumber, authority.uploaded);
      uploadedBytes += Number(authority.uploaded.size || 0);
      continue;
    }

    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, upload.file.size);
    let signedUrl = authority.signedUrl;
    const refreshAuthority = async () => {
      authority = await authorizeS3Part(upload, partNumber);
      if (authority.uploaded) return;
      signedUrl = authority.signedUrl;
    };

    const response = await fetchWithRetry(
      () => {
        if (authority.uploaded) {
          // Authorization can discover that a timed-out PUT actually succeeded.
          // A synthetic accepted response avoids uploading the part twice.
          return Promise.resolve(new Response(null, { status: 208 }));
        }
        return options.fetchImpl(signedUrl, {
          method: 'PUT',
          body: upload.file.slice(start, end),
          mode: 'cors',
          signal: options.signal,
        });
      },
      {
        ...options,
        operation: `Uploading S3 part ${partNumber}`,
        accepted: (candidate) => candidate.ok,
        refreshAuthority,
      }
    );

    // The browser only checks that CORS exposed an ETag as proof of an observable
    // UploadPart success. The backend later re-lists trusted part state from S3.
    if (!authority.uploaded && !response.headers.get('ETag')) {
      throw new Error(
        `S3 uploaded part ${partNumber} but did not expose its ETag. Check bucket CORS.`
      );
    }
    uploadedBytes += end - start;
    await reportProgress(
      upload,
      upload.api,
      upload.onProgress,
      uploadedBytes,
      false,
      options.signal
    );
  }
}

async function runS3Multipart(upload, options) {
  // If authorization replaces an expired multipart session, discard all old
  // part bookkeeping and replay once against the new upload ID.
  for (let restart = 0; restart < 2; restart += 1) {
    try {
      await runS3MultipartSession(upload, options);
      return;
    } catch (error) {
      if (!(error instanceof UploadSessionRestartError) || restart > 0) {
        throw error;
      }
    }
  }
}

module.exports = { runS3Multipart, runS3SinglePart };
